import { type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DataConfirmation } from '@domain/entities/DataConfirmation';
import { needsConfirmation } from '@domain/entities/DataConfirmation';
import { getServices } from '@infrastructure/services';
import { useSessionState } from '../../providers/SessionProvider';
import { ConfirmDataModal } from './ConfirmDataModal';

/**
 * Quién ve la tarjeta de confirmación, y cuándo.
 *
 * **Sólo con la sesión lista.** Una cuenta `pending` —recién creada y sin validar—
 * no tiene rol acuñado y las reglas le niegan cada lectura: enseñarle la tarjeta
 * sería pedirle que confirme ocho campos que no puede leer, y su ficha además
 * está a punto de cambiar cuando alguien la valide. Quien está inactivo tampoco:
 * ya no trabaja aquí.
 *
 * **Un error de lectura no abre la tarjeta.** `getFor` devuelve `null` cuando la
 * persona no ha contestado, y eso —o una versión vieja— es lo único que la abre; si la consulta falla
 * —red, permisos— el estado es `error` y la pantalla se queda como estaba. Sin
 * esa distinción, un fallo de red le sacaría la tarjeta obligatoria a alguien que
 * ya la contestó, y esta vez sin poder cerrarla.
 */
/**
 * La decisión, aparte de React para poder probarla.
 *
 * Son tres estados y sólo uno abre la tarjeta, pero los otros dos se confunden
 * con facilidad: al fallar la lectura `data` es `undefined`, no `null`, y
 * cualquier comprobación escrita como «si no hay dato, pregunta» abre un diálogo
 * sin salida cada vez que se cae la red — a quien ya contestó incluido. Aquí el
 * error se nombra en vez de deducirse de la ausencia del dato.
 *
 * **`undefined` y `null` no son lo mismo, y la diferencia es el bug.** `null` es
 * «leí bien y no ha contestado»; `undefined` es «no llegué a leer». Sólo el
 * primero abre la tarjeta — por eso el `=== undefined` de abajo va antes de
 * preguntarle nada a `needsConfirmation`, que no sabe de redes caídas.
 *
 * Quién decide si hace falta contestar es `needsConfirmation`, en el dominio: no
 * es sólo «¿existe el documento?» desde que hay versiones, y esa regla también la
 * aplica el servidor.
 */
export const shouldAsk = (query: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly data: DataConfirmation | null | undefined;
}): boolean => {
  if (query.isPending || query.isError) return false;
  if (query.data === undefined) return false;
  return needsConfirmation(query.data);
};

/**
 * `children` es lo que sale **después**: se pinta sólo cuando ya se leyó la
 * confirmación y no hay nada que preguntar. Lo usa la felicitación de
 * cumpleaños, para no abrir una segunda ventana encima de ésta.
 */
export const ConfirmDataGate = ({ children }: { readonly children?: ReactNode }): ReactNode => {
  const { status } = useSessionState();
  const queryClient = useQueryClient();
  const person = status.kind === 'ready' ? status.person : null;

  const confirmation = useQuery({
    queryKey: ['dataConfirmation', person?.id ?? ''],
    enabled: person !== null,
    /* Una vez por sesión basta: la respuesta sólo cambia cuando esta misma
       pantalla la escribe, y entonces se invalida a mano. */
    staleTime: Infinity,
    queryFn: async () => {
      if (person === null) return null;
      const result = await getServices().dataConfirmations.getFor(person.id);
      if (!result.ok) {
        /*
         * El fallo se calla en pantalla, pero no en la consola.
         *
         * No abrir la tarjeta ante un error es deliberado —ver `shouldAsk`— y
         * tiene un precio: desde fuera, «la lectura falló» y «esta persona ya
         * contestó» se ven exactamente igual, que es una pantalla correcta y
         * vacía de las que este repositorio ya rastrea. Este renglón es lo único
         * que las distingue cuando alguien dice «a mí no me salió nada».
         */
        console.warn('[confirmación de datos] no se pudo leer', {
          personId: person.id,
          error: result.error,
        });
        throw new Error('No pudimos leer tu confirmación de datos.');
      }
      return result.value;
    },
  });

  if (person === null || confirmation.isPending) return null;

  if (
    !shouldAsk({
      isPending: confirmation.isPending,
      isError: confirmation.error !== null,
      data: confirmation.data,
    })
  ) {
    return children ?? null;
  }

  return (
    <ConfirmDataModal
      person={person}
      onDone={() => {
        void queryClient.invalidateQueries({ queryKey: ['dataConfirmation', person.id] });
      }}
    />
  );
};
